export const STUDY_STATUS = {
  CURRENT: 'current',
  FINISHED: 'finished',
  NOT_STUDYING: 'not-studying'
};

export const VALID_PREFIXES = ['BS', 'BA1', 'BA2', 'BA3', 'BA5', 'BA14', 'BA15'];

export function getInitialState() {
  return {
    isOpen: false,
    mode: 'contextual',
    currentStep: 'T1',
    history: [],
    outcome: null,
    answers: {
      studyStatus: '',
      schoolResult: null,
      postcodeResult: null,
      bristolProgramme: '',
      freeSchoolMeals: '',
      careExperience: ''
    }
  };
}

export const questions = {
  Q1: {
    id: 'Q1',
    type: 'radio',
    answerKey: 'studyStatus',
    title: 'Which best describes your current or recent study?',
    error: 'Please choose an option',
    options: [
      { label: "I'm currently studying A-levels or the International Baccalaureate (IB)", value: STUDY_STATUS.CURRENT },
      { label: 'I finished A-levels or the IB in the last academic year', value: STUDY_STATUS.FINISHED },
      { label: "I'm not studying A-levels or the IB", value: STUDY_STATUS.NOT_STUDYING }
    ]
  },
  Q5: {
    id: 'Q5',
    type: 'school',
    titleCurrent: 'Which school or college do you go to?',
    titleFinished: 'Which school or college did you go to?',
    helper: 'We’ll use this to check if your school or college is eligible.',
    label: 'Enter the name of your school or college',
    validationError: 'Please search for your school or college, or select “I can’t find my school or college”',
    noResults: 'We cannot find your school or college. Please try again, or go to the next question.',
    skipLabel: 'I can’t find my school or college',
    summarySkipLabel: 'I can’t find my school or college'
  },
  Q4: {
    id: 'Q4',
    type: 'postcode',
    title: 'Where do you live?',
    helper: 'We use your postcode to check whether you live in an area that meets our contextual offer criteria.',
    label: 'Enter your postcode',
    placeholder: 'For example: BS8 1TH',
    validationError: 'Please enter your postcode, or select “Continue without a postcode”',
    skipLabel: 'Continue without a postcode'
  },
  Q3: {
    id: 'Q3',
    type: 'radio',
    answerKey: 'bristolProgramme',
    title: 'Have you taken part in any of these programmes at Bristol in the last 2 years?',
    error: 'Please choose an option',
    links: [
      { text: 'Access to Bristol', url: 'https://www.bristol.ac.uk/study/outreach/post-16/access/' },
      { text: 'Bristol Scholars', url: 'https://www.bristol.ac.uk/study/outreach/post-16/scholars/' },
      { text: 'Discover Bristol', url: 'https://www.bristol.ac.uk/study/outreach/post-16/discover-bristol/' },
      { text: 'Insight into Bristol summer school', url: 'https://www.bristol.ac.uk/study/outreach/post-16/insight/' },
      { text: 'Next Step Bristol', url: 'https://www.bristol.ac.uk/study/outreach/post-16/nextstep/' },
      { text: 'Virtual Summer School', url: 'https://www.bristol.ac.uk/study/outreach/post-16/virtual-summer-school/' }
    ],
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
      { label: 'I don’t know', value: 'unknown' }
    ]
  },
  Q2: {
    id: 'Q2',
    type: 'radio',
    answerKey: 'freeSchoolMeals',
    title: 'Were you eligible for free school meals at school?',
    helper: 'This applies if you were eligible for free school meals while attending a state school.',
    error: 'Please choose an option',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
      { label: 'I don’t know', value: 'unknown' }
    ]
  },
  Q6: {
    id: 'Q6',
    type: 'radio',
    answerKey: 'careExperience',
    title: 'Have you spent 3 months or more in formalised local authority care?',
    helper: 'You may have spent time living with foster carers under local authority care, in residential care (for example, a children’s home), or looked after under a formalised supervision order.',
    error: 'Please choose an option',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
      { label: 'I don’t know', value: 'unknown' }
    ]
  }
};

const contextualOfferCriteriaLink = '<a href="https://www.bristol.ac.uk/study/undergraduate/entry-requirements-qualifications/contextual-offers/" target="_blank" rel="noopener noreferrer">Check the full contextual offer criteria</a>';
const guaranteedOfferCriteriaLink = '<a href="https://www.bristol.ac.uk/study/undergraduate/entry-requirements-qualifications/guaranteed-offers/" target="_blank" rel="noopener noreferrer">Check the full criteria</a>';
const findCourseLink = '<a href="https://www.bristol.ac.uk/study/undergraduate/search/" target="_blank" rel="noopener noreferrer">find your course</a>';
const criteriaCourseText = `${contextualOfferCriteriaLink}, or ${findCourseLink} for further information.`;
const contactEligibilityText = 'Call the Enquiries Team on <a href="tel:+441173941649">+44 (0) 117 394 1649</a> or email <a href="mailto:choosebristol-ug@bristol.ac.uk">choosebristol-ug@bristol.ac.uk</a> if you have any questions about your eligibility.';

const successOutcome = {
  title: 'You may be eligible for a contextual offer',
  primary: 'Based on your answers, you are eligible for a contextual offer.',
  secondary: `${contextualOfferCriteriaLink}, or ${findCourseLink} for further information.<br><br>You may also be eligible for a guaranteed offer. ${guaranteedOfferCriteriaLink} for further information.<br><br>You do not need to answer any more questions.`,
  eligible: true,
  status: 'success'
};

const notEligibleOutcome = {
  title: 'You are not eligible for a contextual offer',
  primary: 'Based on your answers, you are not eligible for a contextual offer.',
  secondary: `You can still apply if you meet our standard entry requirements. ${criteriaCourseText}<br><br>${contactEligibilityText}`,
  eligible: false,
  status: 'unsuccessful'
};

const unconfirmedOutcome = {
  title: 'We could not confirm your eligibility',
  primary: 'Based on your answers, we could not determine whether you’re eligible for a contextual offer.',
  secondary: `You can still apply if you meet our standard entry requirements. ${criteriaCourseText}<br><br>${contactEligibilityText}`,
  eligible: false,
  status: 'unconfirmed'
};

export const outcomes = {
  O1: notEligibleOutcome,
  O12: notEligibleOutcome,
  O13: unconfirmedOutcome
};

['O2', 'O3', 'O4', 'O5', 'O6', 'O7', 'O8', 'O9', 'O10', 'O11'].forEach((id) => {
  outcomes[id] = successOutcome;
});

function isUnknown(value) {
  return value === 'unknown';
}

function hasUncertainty(answers) {
  const school = answers.schoolResult || {};
  const postcode = answers.postcodeResult || {};

  return Boolean(
    isUnknown(answers.bristolProgramme) ||
    isUnknown(answers.freeSchoolMeals) ||
    isUnknown(answers.careExperience) ||
    school.type === 'unconfirmed' ||
    school.skipped ||
    postcode.skipped
  );
}

function resolveSchoolOutcome(answers) {
  const school = answers.schoolResult || { type: 'not-eligible' };

  // ASSC_STATUS = Y and PROVISIONAL = N is immediately eligible.
  if (school.type === 'aspiring-state') return 'O5';

  // ASSC_STATUS = N or PROVISIONAL = Y are not immediately eligible.
  // Continue the journey so postcode / later questions can still qualify.
  return 'Q4';
}

function resolvePostcodeOutcome(answers) {
  const postcode = answers.postcodeResult || {};

  // IMD quintile <= 2 is immediately eligible.
  if (postcode.eligible) return 'O10';

  return 'Q3';
}

export function getNextStep(currentStep, answers) {
  switch (currentStep) {
    case 'Q1':
      return answers.studyStatus === STUDY_STATUS.NOT_STUDYING ? 'O1' : 'Q5';
    case 'Q5':
      return resolveSchoolOutcome(answers);
    case 'Q4':
      return resolvePostcodeOutcome(answers);
    case 'Q3':
      return answers.bristolProgramme === 'yes' ? 'O3' : 'Q2';
    case 'Q2':
      return answers.freeSchoolMeals === 'yes' ? 'O2' : 'Q6';
    case 'Q6':
      if (answers.careExperience === 'yes') return 'O11';
      return hasUncertainty(answers) ? 'O13' : 'O12';
    default:
      return 'Q1';
  }
}

function optionLabel(questionId, value) {
  const question = questions[questionId];
  const option = question && question.options && question.options.find((item) => item.value === value);
  return option ? option.label : value;
}

function schoolDisplayName(answers) {
  const school = answers.schoolResult || {};
  if (school.skipped) return questions.Q5.summarySkipLabel;
  return school.displayName || school.name || questions.Q5.summarySkipLabel;
}

function schoolStatus(answers) {
  const school = answers.schoolResult;
  if (!school || school.skipped) return { text: '', status: 'unknown' };
  if (school.type === 'aspiring-state') return { text: 'This school or college is on our list of aspiring state schools.', status: 'success' };
  if (school.type === 'unconfirmed') {
    return {
      text: 'We’re not yet able to confirm whether your school or college is on the aspiring state schools list for your year of entry. This will be confirmed when the final list is published later in the year.',
      status: 'unknown'
    };
  }
  return { text: 'This school or college is not on our list of aspiring state schools.', status: 'error' };
}

function normaliseAnswerStatus(status) {
  if (status === 'success') return 'success';
  if (status === 'unknown' || status === 'unconfirmed') return 'unknown';
  return 'error';
}

export function getAnswerSummary(answers, outcomeId) {
  const rows = [];

  // Q1/current study is intentionally excluded from the outcome answer summary.
  if (answers.schoolResult) {
    const isFinished = answers.studyStatus === STUDY_STATUS.FINISHED;
    const schoolNote = schoolStatus(answers);
    rows.push({
      id: 'school',
      question: isFinished ? questions.Q5.titleFinished : questions.Q5.titleCurrent,
      answer: schoolDisplayName(answers),
      note: schoolNote.text,
      noteStatus: normaliseAnswerStatus(schoolNote.status),
      questionOrder: 2
    });
  }

  if (answers.postcodeResult) {
    const skipped = Boolean(answers.postcodeResult.skipped);
    rows.push({
      id: 'postcode',
      question: questions.Q4.title,
      answer: answers.postcodeResult.displayValue || answers.postcodeResult.postcode || questions.Q4.skipLabel,
      note: skipped ? '' : (answers.postcodeResult.note || ''),
      noteStatus: skipped ? 'unknown' : (answers.postcodeResult.eligible ? 'success' : 'error'),
      questionOrder: 3
    });
  }

  if (answers.bristolProgramme) {
    rows.push({
      id: 'bristol-programme',
      question: questions.Q3.title,
      answer: optionLabel('Q3', answers.bristolProgramme),
      noteStatus: answers.bristolProgramme === 'yes' ? 'success' : (answers.bristolProgramme === 'unknown' ? 'unknown' : 'error'),
      questionOrder: 4
    });
  }

  if (answers.freeSchoolMeals) {
    rows.push({
      id: 'free-school-meals',
      question: questions.Q2.title,
      answer: optionLabel('Q2', answers.freeSchoolMeals),
      noteStatus: answers.freeSchoolMeals === 'yes' ? 'success' : (answers.freeSchoolMeals === 'unknown' ? 'unknown' : 'error'),
      questionOrder: 5
    });
  }

  if (answers.careExperience) {
    rows.push({
      id: 'care-experience',
      question: questions.Q6.title,
      answer: optionLabel('Q6', answers.careExperience),
      noteStatus: answers.careExperience === 'yes' ? 'success' : (answers.careExperience === 'unknown' ? 'unknown' : 'error'),
      questionOrder: 6
    });
  }

  const outcome = outcomes[outcomeId] || {};

  if (outcome.status === 'success' || outcome.eligible === true) {
    return rows.slice().sort((a, b) => (b.questionOrder || 0) - (a.questionOrder || 0));
  }

  return rows.slice().sort((a, b) => (a.questionOrder || 99) - (b.questionOrder || 99));
}
